/**
 * Route configuration and pure navigation helpers.
 *
 * Extracted from cairn-app.ts so routing logic can be unit-tested without
 * spinning up a full component tree.
 */

export interface RouteDefinition {
  path: string
  component: string
  title: string
}

/**
 * Canonical route table used by the application router.
 */
export const routes: readonly RouteDefinition[] = [
  { path: '/', component: 'cairn-map', title: 'Map' },
  { path: '/map', component: 'cairn-map', title: 'Map' },
  // Trips — Activities, Plans, Routes
  { path: '/activities', component: 'cairn-logbook', title: 'Trips' },
  { path: '/activities/:id', component: 'activity-detail', title: 'Activity' },
  { path: '/activities/:id/map', component: 'activity-map-page', title: 'Activity Map' },
  { path: '/plans', component: 'cairn-logbook', title: 'Trips' },
  { path: '/plans/new', component: 'plan-form', title: 'New Plan' },
  { path: '/plans/:id', component: 'plan-detail', title: 'Plan' },
  { path: '/plans/:id/pack-list', component: 'plan-detail', title: 'Plan' },
  { path: '/plans/:id/conditions', component: 'plan-detail', title: 'Plan' },
  { path: '/plans/:id/field-log', component: 'plan-detail', title: 'Plan' },
  { path: '/plans/:id/decisions', component: 'plan-detail', title: 'Plan' },
  { path: '/plans/:id/edit', component: 'plan-form', title: 'Edit Plan' },
  { path: '/plans/:id/map', component: 'plan-map', title: 'Plan Route' },
  { path: '/plans/:id/download', component: 'plan-download', title: 'Download Plan' },
  { path: '/routes', component: 'cairn-logbook', title: 'Trips' },
  { path: '/routes/:id', component: 'route-detail', title: 'Route' },
  { path: '/plans/invite/:token', component: 'cairn-team-invite-claim', title: 'Join Plan' },
  { path: '/teams', component: 'cairn-teams', title: 'Teams' },
  { path: '/teams/invite/:token', component: 'cairn-team-invite-claim', title: 'Join Team' },
  { path: '/teams/:id', component: 'cairn-team-detail', title: 'Team' },
  { path: '/settings', component: 'cairn-settings', title: 'Settings' },
  { path: '/settings/profile', component: 'cairn-settings', title: 'Profile' },
  {
    path: '/settings/preferences/units',
    component: 'cairn-settings',
    title: 'Units & Display',
  },
  { path: '/settings/preferences/theme', component: 'cairn-settings', title: 'Appearance' },
  { path: '/settings/preferences/tracking', component: 'cairn-settings', title: 'Tracking' },
  { path: '/settings/permissions', component: 'cairn-settings', title: 'Permissions' },
  { path: '/settings/privacy', component: 'cairn-settings', title: 'Privacy' },
  { path: '/settings/security', component: 'cairn-settings', title: 'Security' },
  { path: '/settings/storage', component: 'cairn-settings', title: 'Storage' },
  { path: '/settings/subscription', component: 'cairn-settings', title: 'Subscription' },
  {
    path: '/subscription/success',
    component: 'subscription-success',
    title: 'Subscription Confirmed',
  },
  { path: '/help', component: 'cairn-docs', title: 'Help' },
  { path: '/auth/login', component: 'auth-login', title: 'Sign In' },
  { path: '/auth/register', component: 'auth-register', title: 'Create Account' },
  { path: '/auth/setup', component: 'auth-account-setup', title: 'Account Setup' },
  { path: '/auth/eula', component: 'eula-acceptance', title: 'Terms of Service' },
  { path: '/auth/callback', component: 'auth-callback', title: 'Signing in' },
  {
    path: '/auth/register-passkey',
    component: 'auth-register-passkey',
    title: 'Register Passkey',
  },
] as const

/**
 * Top-level view sections. Sub-routes within a section share the same view
 * name (e.g. `/trips/new` still maps to `'trips'`).
 */
const VIEW_PREFIXES: readonly [string, string][] = [
  ['/activities', 'trips'],
  ['/plans', 'trips'],
  ['/routes', 'trips'],
  ['/teams', 'teams'],
  ['/settings', 'settings'],
  ['/help', 'help'],
]

/**
 * Map a URL pathname to the active navigation view name.
 *
 * Returns one of: `'map'`, `'trips'`, `'teams'`, `'settings'`.
 */
export function getCurrentView(pathname: string): string {
  const normalized = normalizePath(pathname)

  for (const [prefix, view] of VIEW_PREFIXES) {
    if (normalized === prefix || normalized.startsWith(prefix + '/')) {
      return view
    }
  }

  return 'map'
}

/**
 * Derive the page title shown in the header for a given pathname.
 */
export function getCurrentPageTitle(pathname: string): string {
  const normalized = normalizePath(pathname)
  const view = getCurrentView(normalized)

  switch (view) {
    case 'trips':
      if (/^\/plans\/[^/]+\/map$/.test(normalized)) {
        return 'Plan Route'
      }
      if (/^\/plans\/[^/]+\/download$/.test(normalized)) {
        return 'Download Plan'
      }
      return 'Trips'
    case 'teams':
      if (/^\/teams\/invite\//.test(normalized)) {
        return 'Join Team'
      }
      if (/^\/teams\/[^/]+$/.test(normalized) && normalized !== '/teams') {
        return 'Team'
      }
      return 'Teams'
    case 'settings':
      if (normalized === '/settings/profile') {
        return 'Profile'
      }
      if (normalized === '/settings/preferences/units') {
        return 'Units & Display'
      }
      if (normalized === '/settings/preferences/theme') {
        return 'Appearance'
      }
      if (normalized === '/settings/preferences/tracking') {
        return 'Tracking'
      }
      if (normalized === '/settings/permissions') {
        return 'Permissions'
      }
      if (normalized === '/settings/privacy') {
        return 'Privacy'
      }
      if (normalized === '/settings/security') {
        return 'Security'
      }
      if (normalized === '/settings/storage') {
        return 'Storage'
      }
      return 'Settings'
    case 'help':
      return 'Help'
    case 'map':
    default:
      return 'Map'
  }
}

/**
 * Top-level navigation destinations that do NOT show a back button in the
 * header (i.e. they are reachable directly from the bottom nav).
 */
const TOP_LEVEL_PATHS = new Set(['/', '/map'])

/**
 * Determine whether the header should show a back button for the given
 * pathname. Top-level views (`/`, `/map`) do not need one; everything else
 * does.
 */
export function needsBackButton(pathname: string): boolean {
  const normalized = normalizePath(pathname)
  if (TOP_LEVEL_PATHS.has(normalized)) {
    return false
  }
  // Any non-map route shows a back button
  return getCurrentView(normalized) !== 'map'
}

/**
 * Map from navigation view name to the canonical path used by `navigateTo`.
 */
export const viewToPath: Record<string, string> = {
  map: '/',
  trips: '/activities',
  teams: '/teams',
  settings: '/settings',
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Strip a single trailing slash (unless the path *is* `/`).
 */
function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1)
  }
  return pathname || '/'
}
